import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

export default function CTA() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        window.gtag('event', 'form_submit', {
          form_type: 'cta_inquiry',
          form_location: 'homepage_cta',
        });
        setModalType('success');
        setShowModal(true);
        form.reset();
      })
      .catch(() => {
        setModalType('error');
        setShowModal(true);
      });
  };

  return (
    <section id="cta" className="py-16 md:py-24 bg-stone-50/60 relative overflow-hidden" ref={ref}>
      {/* Thank You Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl md:rounded-3xl p-8 md:p-12 max-w-md w-full shadow-2xl text-center"
            >
              {modalType === 'success' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15, ...springTransition }}
                    className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500 rounded-full mx-auto mb-6 shadow-lg"
                  >
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-check-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
                    Thank You!
                  </h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Your inquiry has been submitted successfully. Our team will reach out to you within 24 hours.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-red-500 rounded-full mx-auto mb-6 shadow-lg">
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-error-warning-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">
                    Oops!
                  </h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Something went wrong. Please try again or email us directly at{' '}
                    <a href="mailto:info@thoughtic.com" className="text-primary-600 hover:underline">info@thoughtic.com</a>.
                  </p>
                </>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                {modalType === 'success' ? 'Got It' : 'Try Again'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute top-40 right-10 w-72 h-72 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.8 }}
          className="bg-navy-900 rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl"
        >
          <div className="relative px-6 py-10 sm:px-8 sm:py-14 lg:px-16 lg:py-20">
            {/* Subtle Overlay */}
            <div className="absolute inset-0 bg-primary-600/10"></div>
            
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.2' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
            }}></div>

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="text-white">
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...springTransition, duration: 0.8, delay: 0.15 }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.2, ...springTransition }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-4 md:mb-6"
                  >
                    <i className="ri-vip-crown-line text-amber-400"></i>
                    <span className="text-xs md:text-sm font-semibold">Get Started Today</span>
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 25 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.25, ...springTransition }}
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6 leading-tight"
                  >
                    Let's Build Software
                    <br />
                    <span className="text-primary-400">
                      or Scale Your Team
                    </span>
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="text-sm md:text-lg text-gray-300 mb-6 md:mb-8 leading-relaxed"
                  >
                    Whether you need a product built from scratch, a feature shipped, or a vetted engineer on your team — Thoughtic Solutions is ready to help you move faster and scale with confidence.
                  </motion.p>

                  {/* Feature List */}
                  <div className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                    {[
                      'Contact Us Today',
                      'Schedule a Consultation',
                      'Discuss Your Project Needs',
                      'Build Software or Hire Talent',
                    ].map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -30 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ ...springTransition, duration: 0.5, delay: 0.35 + index * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={inView ? { opacity: 1, scale: 1 } : {}}
                          transition={{ delay: 0.4 + index * 0.1, ...springTransition }}
                          className="w-5 h-5 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0"
                        >
                          <i className="ri-check-line text-xs text-white"></i>
                        </motion.div>
                        <span className="text-xs md:text-sm text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Trust Badges */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="flex flex-wrap items-center gap-4 md:gap-6 pt-4 md:pt-6 border-t border-white/10"
                  >
                    {[
                      { icon: 'ri-code-box-line', label: 'Full-Stack Delivery' },
                      { icon: 'ri-time-line', label: '5-Day Onboarding' },
                      { icon: 'ri-star-fill', label: '98% Retention' },
                    ].map((badge, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.65 + i * 0.06, duration: 0.4 }}
                        className="flex items-center gap-2"
                      >
                        <i className={`${badge.icon} text-primary-400 text-lg md:text-xl`}></i>
                        <span className="text-xs md:text-sm text-gray-400">{badge.label}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>
              </div>

              {/* Right Form */}
              <motion.div
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
                transition={{ ...springTransition, duration: 0.8, delay: 0.3 }}
                className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl"
              >
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="text-center mb-4 md:mb-6"
                >
                  <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">Get Started Today</h3>
                  <p className="text-xs md:text-sm text-gray-600">Fill in your details and we will get back to you within 24 hours</p>
                </motion.div>

                <form 
                  className="space-y-3 md:space-y-4"
                  method="POST"
                  action="https://readdy.ai/api/form/d87u5nhjomn5g3harc70"
                  encType="application/x-www-form-urlencoded"
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.45, duration: 0.4 }}
                  >
                    <label htmlFor="name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                      placeholder="John Doe"
                      required
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <label htmlFor="email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Work Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                      placeholder="you@company.com"
                      required
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.55, duration: 0.4 }}
                  >
                    <label htmlFor="company" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                      placeholder="Acme Inc."
                      required
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    <label htmlFor="requirement" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Service Interest
                    </label>
                    <select
                      id="requirement"
                      name="requirement"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    >
                      <optgroup label="Strategy & Design">
                        <option value="Product Discovery & Strategy">Product Discovery & Strategy</option>
                        <option value="UI/UX Design Services">UI/UX Design Services</option>
                      </optgroup>
                      <optgroup label="Development">
                        <option value="Custom Software Development">Custom Software Development</option>
                        <option value="SaaS Product Development">SaaS Product Development</option>
                        <option value="Web Application Development">Web Application Development</option>
                        <option value="Mobile App Development">Mobile App Development</option>
                        <option value="API & System Integrations">API & System Integrations</option>
                      </optgroup>
                      <optgroup label="Cloud & AI">
                        <option value="Cloud & DevOps Engineering">Cloud & DevOps Engineering</option>
                        <option value="AI & Automation Solutions">AI & Automation Solutions</option>
                      </optgroup>
                      <optgroup label="Quality & Support">
                        <option value="QA & Testing Services">QA & Testing Services</option>
                        <option value="Product Maintenance & Support">Product Maintenance & Support</option>
                      </optgroup>
                      <optgroup label="Talent Solutions">
                        <option value="Dedicated Developers">Dedicated Developers</option>
                        <option value="Contract Staffing">Contract Staffing</option>
                        <option value="Offshore Development Teams">Offshore Development Teams</option>
                        <option value="Project-Based Engagement">Project-Based Engagement</option>
                      </optgroup>
                    </select>
                  </motion.div>

                  <motion.button
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.65, duration: 0.4 }}
                    type="submit"
                    className="w-full py-3 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                  >
                    Schedule a Consultation
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
                  </motion.button>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.7, duration: 0.4 }}
                    className="text-xs text-center text-gray-500 mt-3 md:mt-4"
                  >
                    By submitting, you agree to our{' '}
                    <Link to="/terms" className="text-primary-600 hover:underline cursor-pointer">Terms</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-primary-600 hover:underline cursor-pointer">Privacy Policy</Link>
                  </motion.p>
                </form>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.8, delay: 0.5 }}
          className="mt-10 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8"
        >
          {[
            { icon: 'ri-user-add-line', value: '200+', label: 'Developers Placed' },
            { icon: 'ri-building-2-line', value: '50+', label: 'Active Clients' },
            { icon: 'ri-briefcase-line', value: '10+', label: 'Industries Served' },
            { icon: 'ri-star-fill', value: '4.9/5', label: 'Client Rating' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.08, ...springTransition }}
              className="text-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.65 + index * 0.08, ...springTransition }}
                className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-50 rounded-xl mx-auto mb-2 md:mb-3"
              >
                <i className={`${stat.icon} text-lg md:text-2xl text-primary-600`}></i>
              </motion.div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5 md:mb-1">{stat.value}</div>
              <div className="text-xs md:text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}